import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Wifi, WifiOff, X } from "lucide-react";
import { useCryptoStream, STREAMED_COINS } from "../context/CryptoStreamContext";
import { getPref, setPref } from "../lib/prefs";

const formatPrice = (p) => {
  if (p == null) return "—";
  if (p >= 1000) return p.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (p >= 1) return p.toFixed(2);
  return p.toFixed(4);
};

/**
 * Floating bottom-of-screen ticker bar showing live Binance prices for the top 10.
 * Collapsible. Persists collapse state in a cookie pref.
 */
export default function LiveTickerBar() {
  const { prices, status } = useCryptoStream();
  const [collapsed, setCollapsed] = useState(() => getPref("ticker_collapsed") === true);
  const [hidden, setHidden] = useState(() => getPref("ticker_hidden") === true);

  const items = useMemo(
    () =>
      STREAMED_COINS.map((c) => prices[c.symbol]).filter(
        (p) => p && Number.isFinite(p.price)
      ),
    [prices]
  );

  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    setPref("ticker_collapsed", next);
  };

  const close = () => {
    setHidden(true);
    setPref("ticker_hidden", true);
  };

  if (hidden) return null;

  return (
    <div
      data-testid="live-ticker-bar"
      className="hidden md:block fixed bottom-0 left-0 right-0 z-40 bg-[#2A2F2B] text-white border-t border-[#1F2422] shadow-2xl"
    >
      {!collapsed && (
        <div className="overflow-hidden">
          <div
            className="flex items-center gap-6 py-2.5 px-4 animate-marquee whitespace-nowrap"
            data-testid="live-ticker-marquee"
          >
            {[...items, ...items].map((p, idx) => (
              <Link
                key={`${p.symbol}-${idx}`}
                to={`/asset/crypto/${p.symbol}`}
                data-testid={`live-ticker-item-${p.symbol}`}
                className="flex items-center gap-2 group"
              >
                <span className="text-[10px] uppercase tracking-[0.2em] font-mono-ticker text-white/60">
                  {p.symbol}
                </span>
                <span className="font-mono-ticker text-sm font-semibold tabular-nums">
                  ${formatPrice(p.price)}
                </span>
                <span
                  className="font-mono-ticker text-xs tabular-nums"
                  style={{ color: p.change24h >= 0 ? "#7BC79E" : "#E89B8B" }}
                >
                  {p.change24h >= 0 ? "+" : ""}
                  {p.change24h?.toFixed(2)}%
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
        <span
          className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-white/50"
          title={
            status === "connected"
              ? "Live Binance stream"
              : items.length
              ? "Showing latest snapshot — live stream unavailable"
              : `Status: ${status}`
          }
        >
          {status === "connected" ? (
            <Wifi className="w-3 h-3" strokeWidth={2} style={{ color: "#7BC79E" }} />
          ) : (
            <WifiOff
              className="w-3 h-3"
              strokeWidth={2}
              style={{ color: items.length ? "#D4B07A" : "#E89B8B" }}
            />
          )}
          {status === "connected"
            ? "Live"
            : items.length
            ? "Snapshot"
            : status}
        </span>
        <button
          onClick={toggleCollapsed}
          data-testid="live-ticker-collapse-btn"
          className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white"
          aria-label={collapsed ? "Expand ticker" : "Collapse ticker"}
          title={collapsed ? "Expand" : "Collapse"}
        >
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${
              collapsed ? "rotate-180" : ""
            }`}
            strokeWidth={2}
          />
        </button>
        <button
          onClick={close}
          data-testid="live-ticker-close-btn"
          className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white"
          aria-label="Hide ticker"
          title="Hide"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      </div>
      {collapsed && (
        <div className="px-4 py-2 text-[11px] text-white/60 flex items-center gap-2">
          <Wifi
            className="w-3 h-3"
            strokeWidth={2}
            style={{ color: status === "connected" ? "#7BC79E" : "#E89B8B" }}
          />
          Live ticker minimized — click ▲ to expand
        </div>
      )}
    </div>
  );
}
